import React, { useState } from "react";
import { Badge, Button, Modal, Table } from "react-bootstrap";
import { Code } from "react-feather";
import { settingsType, useSettings } from "../../hooks";

// ── CouponRole ────────────────────────────────────────────────────────────────

const COUPON_ROLE_LABELS: Record<number, { label: string; bg: string }> = {
  0: { label: "Listed", bg: "secondary" },
  1: { label: "AutoCoupon", bg: "primary" },
  2: { label: "Fallback", bg: "warning" },
};

function couponRoleBadge(role?: number) {
  if (role === undefined || role === null)
    return <span className="text-muted">—</span>;
  const r = COUPON_ROLE_LABELS[role] ?? { label: `${role}`, bg: "secondary" };
  return <Badge bg={r.bg}>{r.label}</Badge>;
}

// ── Shape helpers ──────────────────────────────────────────────────────────────

interface CouponCode {
  id?: string;
  code: string;
  priority?: number;
  isSiteWide?: boolean;
  role?: number;
  summary?: string;
}

interface Offer {
  id?: string;
  summary?: string;
  priority?: number;
  isSiteWide?: boolean;
  validTo?: string;
}

interface Template {
  priority?: number;
  version?: number;
  checkoutPageUrlRegex?: string;
  couponBoxSelector?: string;
  submitCodeButtonSelector?: string;
  [key: string]: unknown;
}

interface FindCouponsResponse {
  couponCodes?: CouponCode[];
  templates?: Template[];
  offers?: Offer[];
  [key: string]: unknown;
}

function isFriendlyResponse(r: unknown): r is FindCouponsResponse {
  if (!r || typeof r !== "object") return false;
  const obj = r as Record<string, unknown>;
  return (
    Array.isArray(obj.couponCodes) ||
    Array.isArray(obj.templates) ||
    Array.isArray(obj.offers)
  );
}

// ── Template detail modal ─────────────────────────────────────────────────────

const TemplatesModal: React.FC<{
  templates: Template[];
  show: boolean;
  onHide: () => void;
}> = ({ templates, show, onHide }) => (
  <Modal show={show} onHide={onHide} size="xl" scrollable>
    <Modal.Header closeButton>
      <Modal.Title>Templates ({templates.length})</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {templates.map((t, i) => (
        <div key={i} className="mb-4">
          <h6 className="mb-2">
            Template {i + 1}{" "}
            <Badge bg="secondary" className="me-1">
              v{t.version ?? "?"}
            </Badge>
            <Badge bg="info">priority {t.priority ?? "?"}</Badge>
          </h6>
          <pre
            className="bg-light p-2 rounded small"
            style={{ maxHeight: 300, overflow: "auto" }}
          >
            {JSON.stringify(t, null, 2)}
          </pre>
        </div>
      ))}
    </Modal.Body>
  </Modal>
);

// ── Main component ────────────────────────────────────────────────────────────

interface ResponsePreviewProps {
  responseBody: unknown;
  status: "success" | "error";
  errorMessage?: string | null;
}

export const ResponsePreview: React.FC<ResponsePreviewProps> = ({
  responseBody,
  status,
  errorMessage,
}) => {
  const [mode, setMode] = useSettings(settingsType.BASKET_RESPONSE_MODE);
  const [showTemplates, setShowTemplates] = useState(false);

  const effectiveMode = mode ?? "friendly";
  const friendly = effectiveMode === "friendly";
  const canBeFriendly = isFriendlyResponse(responseBody);

  const coupons: CouponCode[] = canBeFriendly
    ? ((responseBody as FindCouponsResponse).couponCodes ?? [])
    : [];
  const templates: Template[] = canBeFriendly
    ? ((responseBody as FindCouponsResponse).templates ?? [])
    : [];
  const offers: Offer[] = canBeFriendly
    ? ((responseBody as FindCouponsResponse).offers ?? [])
    : [];

  return (
    <div>
      {/* Mode toggle */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <small
          className="text-muted fw-semibold text-uppercase"
          style={{ letterSpacing: "0.05em" }}
        >
          API Response
        </small>
        <div className="d-flex gap-1">
          <Button
            size="sm"
            variant={friendly ? "primary" : "outline-secondary"}
            onClick={() => setMode("friendly")}
            style={{ fontSize: 11, padding: "1px 8px" }}
            disabled={!canBeFriendly}
          >
            Friendly
          </Button>
          <Button
            size="sm"
            variant={!friendly ? "primary" : "outline-secondary"}
            onClick={() => setMode("raw")}
            style={{ fontSize: 11, padding: "1px 8px" }}
          >
            <Code size={11} className="me-1" />
            Raw
          </Button>
        </div>
      </div>

      {errorMessage && status === "error" && (
        <div className="text-danger small mb-2">{errorMessage}</div>
      )}

      {/* Friendly view */}
      {friendly && canBeFriendly ? (
        <div>
          {/* Templates */}
          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="text-muted small fw-semibold">Templates</span>
              {templates.length > 0 ? (
                <Button
                  size="sm"
                  variant="outline-secondary"
                  style={{ fontSize: 11, padding: "1px 8px" }}
                  onClick={() => setShowTemplates(true)}
                >
                  {templates.length} template{templates.length !== 1 ? "s" : ""}{" "}
                  available
                </Button>
              ) : (
                <span className="text-muted small">None</span>
              )}
            </div>
          </div>

          {/* Coupons */}
          <div className="mb-3">
            <div className="text-muted small fw-semibold mb-1">
              Coupon Codes ({coupons.length})
            </div>
            {coupons.length > 0 ? (
              <Table bordered size="sm" responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th style={{ width: 160 }}>Code</th>
                    <th style={{ width: 90 }}>Role</th>
                    <th style={{ width: 60 }}>Sitewide</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {coupons.map((c, i) => (
                    <tr key={c.id ?? i}>
                      <td className="text-muted">{c.priority ?? i}</td>
                      <td>
                        <code className="fw-bold">{c.code}</code>
                      </td>
                      <td>{couponRoleBadge(c.role)}</td>
                      <td className="text-center">
                        {c.isSiteWide ? (
                          <Badge bg="success">Yes</Badge>
                        ) : (
                          <Badge bg="secondary">No</Badge>
                        )}
                      </td>
                      <td className="small text-muted">{c.summary ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted small fst-italic">
                No coupons returned
              </div>
            )}
          </div>

          {/* Offers */}
          {offers.length > 0 && (
            <div>
              <div className="text-muted small fw-semibold mb-1">
                Offers ({offers.length})
              </div>
              <Table bordered size="sm" responsive className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th style={{ width: 80 }}>Valid To</th>
                    <th style={{ width: 60 }}>Sitewide</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((o, i) => (
                    <tr key={o.id ?? i}>
                      <td className="text-muted">{o.priority ?? i}</td>
                      <td className="small">
                        {o.validTo
                          ? new Date(o.validTo).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="text-center">
                        {o.isSiteWide ? (
                          <Badge bg="success">Yes</Badge>
                        ) : (
                          <Badge bg="secondary">No</Badge>
                        )}
                      </td>
                      <td className="small text-muted">{o.summary ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      ) : (
        /* Raw view */
        <pre
          className="bg-light p-2 rounded small mb-0"
          style={{ maxHeight: 350, overflow: "auto" }}
        >
          {JSON.stringify(responseBody, null, 2)}
        </pre>
      )}

      <TemplatesModal
        templates={templates}
        show={showTemplates}
        onHide={() => setShowTemplates(false)}
      />
    </div>
  );
};
